import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './SavedEstimateList.styles';

import { STATUS } from '~/core/constants';
import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import useExpiredEstimateActions from '~/core/hooks/useExpiredEstimateActions';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectCurrentEstimateCode,
  selectEstimateHasBeenSaved,
  updateEstimateHasBeenSaved,
} from '~/redux/slices/estimate';
import InformationModal from '~molecules/InformationModal';
import InputOption from '~molecules/InputOption';

const SavedEstimateList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isEstimateDirty } = useEstimateSaver();
  const {
    fetchedEstimate,
    startNewEstimate,
    switchLoadedEstimateCode,
    isFetchingEstimate,
  } = useEstimateContext();
  const { createCloneEstimate } = useExpiredEstimateActions();

  const currentEstimateCode = useAppSelector(selectCurrentEstimateCode);
  const estimateHasBeenSaved = useAppSelector(selectEstimateHasBeenSaved);
  const [showCompletedModal, setShowCompletedModal] =
    useState(estimateHasBeenSaved);
  const [useCurrentDesign, setUseCurrentDesign] = useState(true);

  // Check if estimate is expired or closed
  const isExpiredOrClosed =
    fetchedEstimate?.status === STATUS.EXPIRED ||
    fetchedEstimate?.status === STATUS.CLOSED;

  const menuItems = useMemo(() => {
    if (!fetchedEstimate) {
      return [];
    }

    const original = {
      label:
        isEstimateDirty && fetchedEstimate.code === currentEstimateCode
          ? `${fetchedEstimate.estimateNumber} - Revised, please save`
          : fetchedEstimate.estimateNumber,
      value: fetchedEstimate.code,
      key: fetchedEstimate.code,
    };

    const revisions = (fetchedEstimate.revisions ?? [])
      .filter((rev) => rev.code !== fetchedEstimate.code)
      .map((version) => ({
        label:
          isEstimateDirty && version.code === currentEstimateCode
            ? `${version.estimateNumber} - Revised, please save`
            : version.estimateNumber,
        value: version.code,
        key: version.code,
      }));

    return [original, ...revisions];
  }, [fetchedEstimate, isEstimateDirty, currentEstimateCode]);

  const currentSelectedValue = useMemo(() => {
    if (!fetchedEstimate) {
      return undefined;
    }

    // currentEstimateCode now contains the GUID, use it directly
    return currentEstimateCode ?? fetchedEstimate.code;
  }, [fetchedEstimate, currentEstimateCode]);

  const fetchedEstimateAddress = useMemo(() => {
    if (!fetchedEstimate) {
      return '';
    }

    const {
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerZip,
    } = fetchedEstimate;

    return `${customerAddress1 || customerAddress2}, ${customerCity}, ${customerState}, ${customerZip}`;
  }, [fetchedEstimate]);

  const handleCloseModal = () => {
    dispatch(updateEstimateHasBeenSaved(false));
    setShowCompletedModal(false);
  };

  const handleRevisionChange = (code: string) => {
    switchLoadedEstimateCode(code);
  };

  const handleStartNewEstimate = useCallback(() => {
    if (isExpiredOrClosed && useCurrentDesign) {
      // If checkbox is checked and estimate is expired/closed, behave like "Copy Estimate & Update Pricing"
      const translationSection =
        fetchedEstimate?.status === STATUS.EXPIRED ? 'EXPIRED' : 'CLOSED';

      createCloneEstimate(translationSection);
    } else {
      // Otherwise, start a new estimate
      startNewEstimate();
    }
  }, [
    isExpiredOrClosed,
    useCurrentDesign,
    fetchedEstimate?.status,
    createCloneEstimate,
    startNewEstimate,
  ]);

  return (
    <>
      {fetchedEstimate && (
        <St.Container>
          <St.Title>{t('ESTIMATE_LIST.TITLE')}</St.Title>
          <St.ContentWrapper>
            <St.RowsContainer>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.ESTIMATE_NUMBER')}:</St.RowTitle>
                <St.SelectList
                  options={menuItems}
                  value={currentSelectedValue}
                  style={{ minWidth: 150 }}
                  onChange={handleRevisionChange}
                  loading={isFetchingEstimate}
                />
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.CUSTOMER_NAME')}:</St.RowTitle>{' '}
                {fetchedEstimate.customerName}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.EMAIL')}:</St.RowTitle>{' '}
                {fetchedEstimate.customerEmail}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.ADDRESS')}:</St.RowTitle>{' '}
                {fetchedEstimateAddress}
              </St.Row>
            </St.RowsContainer>
            <St.ButtonContainer>
              <St.SaveEstimateButton onClick={handleStartNewEstimate}>
                {t('ESTIMATE_LIST.CREATE')}
              </St.SaveEstimateButton>
              {isExpiredOrClosed && (
                <St.CheckboxContainer>
                  <InputOption
                    type="checkbox"
                    value="useCurrentDesign"
                    isChecked={useCurrentDesign}
                    onChange={(e) => setUseCurrentDesign(e.target.checked)}
                    label={t(
                      'SCROLLABLE_AREA.ADDRESS_INFORMATION.NEW_ESTIMATE.CHECKBOX'
                    )}
                  />
                </St.CheckboxContainer>
              )}
            </St.ButtonContainer>
          </St.ContentWrapper>
        </St.Container>
      )}
      <InformationModal
        title={t('ESTIMATE_LIST.MODAL_TITLE')}
        content={t('ESTIMATE_LIST.MODAL_DESCRIPTION')}
        isVisible={showCompletedModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default SavedEstimateList;
